/**
 * DramaScore — json-render web wrapper
 *
 * Web implementation for the json-render catalog (web-only).
 * Visual spec: Sally (Discourse #3543/4) — Section 2
 *
 * Upgraded to match ModernScoreCard visual treatment:
 * - Category-based CSS linear-gradient backgrounds (matching expo-linear-gradient colors)
 * - Large score display with proper typography weight
 * - Category label with icon badge (semi-transparent overlay)
 * - Size variants (sm/md/lg) affecting container and font sizes
 * - Dark theme aesthetic with light text
 *
 * Gradient colors sourced from: apps/mobile/lib/categoryStyles.ts (CATEGORY_STYLES)
 * Web fallback pattern from: apps/mobile/components/glass/GlassCard.tsx
 *
 * Task: [SDUI-M.P3.4b]
 * Agent: Diego <diego@liftping.net>
 */
"use client";

import React from "react";
import { formatDramaScore } from "../../lib/utils";
import type { DramaScoreProps } from "../schema";

/* ------------------------------------------------------------------ */
/*  Category gradient + styling map                                    */
/*  Colors match CATEGORY_STYLES in apps/mobile/lib/categoryStyles.ts    */
/* ------------------------------------------------------------------ */

const categoryStyles: Record<
  string,
  {
    gradient: [string, string];
    label: string;
    icon: string;
    textColor: string;
  }
> = {
  safe: {
    gradient: ["#10B981", "#059669"],
    label: "Low Drama",
    icon: "\u2713",
    textColor: "#FFFFFF",
  },
  caution: {
    gradient: ["#F59E0B", "#D97706"],
    label: "Caution",
    icon: "\u26A0\uFE0F",
    textColor: "#1F2937",
  },
  high_drama: {
    gradient: ["#EF4444", "#DC2626"],
    label: "High Drama",
    icon: "\uD83D\uDD25",
    textColor: "#FFFFFF",
  },
  extreme_drama: {
    gradient: ["#7C3AED", "#5B21B6"],
    label: "Extreme Drama",
    icon: "\uD83D\uDCA5",
    textColor: "#FFFFFF",
  },
};

const defaultCategory = categoryStyles.safe;

/* ------------------------------------------------------------------ */
/*  Size variants                                                      */
/* ------------------------------------------------------------------ */

interface SizeConfig {
  scoreFontSize: number;
  labelFontSize: number;
  badgeFontSize: number;
  paddingVertical: number;
  paddingHorizontal: number;
  borderRadius: number;
  minWidth: number;
  maxWidth: number;
  gap: number;
}

const sizeStyles: Record<"sm" | "md" | "lg", SizeConfig> = {
  sm: {
    scoreFontSize: 36,
    labelFontSize: 11,
    badgeFontSize: 11,
    paddingVertical: 20,
    paddingHorizontal: 24,
    borderRadius: 12,
    minWidth: 140,
    maxWidth: 280,
    gap: 4,
  },
  md: {
    scoreFontSize: 56,
    labelFontSize: 13,
    badgeFontSize: 12,
    paddingVertical: 32,
    paddingHorizontal: 32,
    borderRadius: 16,
    minWidth: 180,
    maxWidth: 360,
    gap: 6,
  },
  lg: {
    scoreFontSize: 72,
    labelFontSize: 15,
    badgeFontSize: 13,
    paddingVertical: 40,
    paddingHorizontal: 40,
    borderRadius: 16,
    minWidth: 200,
    maxWidth: 400,
    gap: 8,
  },
};

/* ------------------------------------------------------------------ */
/*  Component                                                          */
/* ------------------------------------------------------------------ */

export function DramaScore({
  score,
  category,
  size = "md",
  showLabel = true,
}: DramaScoreProps) {
  const catStyle = categoryStyles[category] ?? defaultCategory;
  const sz = sizeStyles[size];
  const formattedScore = formatDramaScore(score);

  const [gradStart, gradEnd] = catStyle.gradient;

  return (
    <div
      style={{
        background: `linear-gradient(135deg, ${gradStart} 0%, ${gradEnd} 100%)`,
        paddingTop: sz.paddingVertical,
        paddingBottom: sz.paddingVertical,
        paddingLeft: sz.paddingHorizontal,
        paddingRight: sz.paddingHorizontal,
        borderRadius: sz.borderRadius,
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        gap: sz.gap,
        minWidth: sz.minWidth,
        maxWidth: sz.maxWidth,
        width: "100%",
        boxShadow: "0 8px 16px rgba(0, 0, 0, 0.3)",
      }}
    >
      {/* Score Number */}
      <span
        style={{
          fontSize: sz.scoreFontSize,
          fontWeight: 700,
          lineHeight: 1,
          letterSpacing: "-0.02em",
          color: catStyle.textColor,
          fontVariantNumeric: "tabular-nums",
        }}
      >
        {formattedScore}
      </span>

      {/* Category Badge */}
      {showLabel && (
        <div
          style={{
            marginTop: 8,
            paddingLeft: 12,
            paddingRight: 12,
            paddingTop: 6,
            paddingBottom: 6,
            borderRadius: 20,
            backgroundColor: "rgba(255, 255, 255, 0.2)",
            display: "flex",
            flexDirection: "row",
            alignItems: "center",
            gap: 6,
          }}
        >
          <span
            style={{
              fontSize: sz.badgeFontSize,
              fontWeight: 600,
              color: catStyle.textColor,
            }}
          >
            {catStyle.icon}
          </span>
          <span
            style={{
              fontSize: sz.badgeFontSize,
              fontWeight: 500,
              letterSpacing: "0.08em",
              textTransform: "uppercase" as const,
              color: catStyle.textColor,
            }}
          >
            {catStyle.label}
          </span>
        </div>
      )}
    </div>
  );
}
